const STORAGE_KEY = "terces_region";
const USA_TIMEZONES = /^America\/(New_York|Chicago|Denver|Los_Angeles|Phoenix|Anchorage|Detroit|Indiana|Kentucky|Boise|Juneau|Menominee|Adak|Honolulu|Sitka|Metlakatla|Nome|Yakutat|North_Dakota)/;

function regionFromCountry(country) {
  return country === "US" ? "usa" : "global";
}

function readOverride() {
  try {
    const params = new URLSearchParams(window.location.search);
    const q = params.get("region");
    if (q === "usa" || q === "global") return q;
  } catch (e) {
    /* no-op */
  }
  return null;
}

function readStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "usa" || v === "global") return v;
  } catch (e) {
    /* localStorage unavailable (private mode) */
  }
  return null;
}

export function storeRegion(region) {
  try {
    localStorage.setItem(STORAGE_KEY, region);
  } catch (e) {
    /* no-op */
  }
}

function timezoneFallback() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (USA_TIMEZONES.test(tz) || tz === "Pacific/Honolulu") return "usa";
    if (tz) return "global";
  } catch (e) {
    /* no-op */
  }
  return null;
}

function languageFallback() {
  const lang = (navigator.language || "").toLowerCase();
  if (lang === "en-us") return "usa";
  if (lang) return "global";
  return null;
}

/**
 * Resolves the visitor's region.
 * Order: build-time pin → URL override → stored choice → IP geolocation
 * (/api/geo, Vercel edge header) → timezone → browser language → global.
 * Never throws; always resolves to "usa" or "global".
 */
export async function detectRegion() {
  // Build-time pin for standalone regional builds.
  if (process.env.REACT_APP_REGION === "usa" || process.env.REACT_APP_REGION === "global") {
    return { region: process.env.REACT_APP_REGION, source: "build" };
  }

  const override = readOverride();
  if (override) {
    storeRegion(override);
    return { region: override, source: "override" };
  }

  const stored = readStored();
  if (stored) return { region: stored, source: "stored" };

  // IP geolocation, 2.5s budget each, silent failure:
  // 1. /api/geo — Vercel serverless (edge header, no external call).
  // 2. api.country.is — free, keyless, CORS-enabled; covers static hosts
  //    (e.g. Coolify/nginx) where /api/geo is swallowed by the SPA fallback.
  const geoSources = [
    { url: "/api/geo", pick: (d) => d && d.country },
    { url: "https://api.country.is/", pick: (d) => d && d.country },
  ];
  for (const { url, pick } of geoSources) {
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 2500);
      const res = await fetch(url, { signal: controller.signal });
      clearTimeout(timer);
      if (res.ok) {
        const country = pick(await res.json());
        if (country) {
          const region = regionFromCountry(country);
          storeRegion(region);
          return { region, source: "ip" };
        }
      }
    } catch (e) {
      /* try next source, then client-side heuristics */
    }
  }

  const tzRegion = timezoneFallback();
  if (tzRegion) {
    storeRegion(tzRegion);
    return { region: tzRegion, source: "timezone" };
  }

  const langRegion = languageFallback();
  if (langRegion) {
    storeRegion(langRegion);
    return { region: langRegion, source: "language" };
  }

  return { region: "global", source: "default" };
}
